import { CollarsTransactionDelta } from './collars-after-state.models';
import { SolutionOrderLegDto } from '../../adjustment-pricing-grid/model/SolutionOrderLegDto';
import { SolutionOrderLegActions } from '../../adjustment-pricing-grid/model/SolutionOrderLegActions';
import { CashFlowAdjustment } from '../../adjustment-pricing-grid/model/CashFlowAdjustment';
import { parseOptionTicker } from '../../options-common/options.model';
import { convertIsoExpirationToFriendly, determineDateFormat } from '../../utils';

const KIND_OPTION = 1;
const SIDE_BUY = 1;

/**
 * Adapts the derived Collars transaction (CollarsTransactionDelta[]) into the CashFlowAdjustment carrier
 * CopyOrdersToService consumes, so the operator can push the planned legs to ThinkOrSwim.
 *
 * The dialog calls copyToTOSDesktop (the OS-clipboard path), and makeEquityTosDesktopOrderLine READS
 * leg.type / leg.strike / leg.expiration to compose the order text — these fields are LOAD-BEARING here
 * (an earlier copyToETS-era note claimed they were unread; that path is no longer the one used):
 *   - expiration MUST be the FRIENDLY 'dd-MMM-yy' form — the builder parses it with
 *     DateTime.fromFormat(x, 'dd-MMM-yy'); an ISO 'yyyy-MM-dd' string renders 'Invalid DateTime' into the
 *     copied order. Collars-composed tickers carry ISO dates, so toLeg converts ISO -> friendly.
 *   - type/strike must be non-null — the builder calls type.toUpperCase() unguarded.
 *
 * OPTION LEGS ONLY: the TOS-Desktop line builders compose option orders; an asset/stock delta (kind 0,
 * no type/strike/expiration) would throw inside the builder and kill the whole copy. toCopyOrdersInput
 * therefore DROPS non-option deltas — the caller decides how to message the skip (the dialog toasts the
 * skipped-asset count so the operator enters stock orders in TOS manually).
 *
 * What copyToETS-style consumers read off the carrier still holds (mainLegs/linkedLegs/..., isPriceToOpen
 * FALSE => hcf=1/orderQty=1, side keyed off action containing 'Buy'/'Sell').
 *
 * All Collars legs go in mainLegs (a Collars transaction is one flat set of fills — no main/linked split).
 * Limit price = the live reference quote when the caller supplies one (keyed by tradingInstrumentCode in
 * `prices`); falls back to the 0 placeholder when no quote is in hand for that leg, in which case the
 * operator sets the price in TOS.
 */
export class CollarsCopyToTosAdapter {

   /**
    * Build the CopyOrdersToService carrier from the derived transaction deltas. OPTION deltas only —
    * asset/stock deltas are dropped here (see the class doc); use countAssetLegs() to message the skip.
    *
    * @param prices optional per-leg live reference quote keyed by tradingInstrumentCode. When present and
    *               positive, it becomes the TOS leg limit; otherwise the leg keeps the 0 placeholder.
    */
   static toCopyOrdersInput(
      deltas: CollarsTransactionDelta[],
      prices?: { [tradingInstrumentCode: string]: number }
   ): CashFlowAdjustment {
      const mainLegs = (deltas || [])
         .filter(d => d.kind === KIND_OPTION)
         .map(d => CollarsCopyToTosAdapter.toLeg(d, prices));
      const adjustment = new CashFlowAdjustment();
      adjustment.mainLegs = mainLegs;
      adjustment.linkedLegs = [];
      adjustment.secondLinkedLegs = [];
      adjustment.thirdLinkedLegs = [];
      // FALSE => copyToETS uses hcf=1/orderQty=1 and maps each leg's |qty| straight to netPosition.
      adjustment.isPriceToOpen = false;
      // First leg's underlying is a convenient carrier-level underlying (unread by copyToETS, set for parity).
      adjustment.underlying = mainLegs.length > 0 ? mainLegs[0].underlying : null;
      return adjustment;
   }

   /** How many deltas toCopyOrdersInput would DROP (asset/stock legs) — for the caller's skip message. */
   static countAssetLegs(deltas: CollarsTransactionDelta[]): number {
      return (deltas || []).filter(d => d.kind !== KIND_OPTION).length;
   }

   /** Map one derived delta to a SolutionOrderLegDto. */
   private static toLeg(
      d: CollarsTransactionDelta,
      prices?: { [tradingInstrumentCode: string]: number }
   ): SolutionOrderLegDto {
      const isOption = d.kind === KIND_OPTION;
      // An option ticker parses into {underlying,...}; an asset code IS its own underlying.
      const parsed = isOption ? parseOptionTicker(d.tradingInstrumentCode) : undefined;
      const underlying = parsed ? parsed.underlying : d.tradingInstrumentCode;
      const type: 'Call' | 'Put' = (d.optionType as ('Call' | 'Put'))
         || (parsed ? (parsed.type as ('Call' | 'Put')) : null);
      const strike = (d.strike != null)
         ? String(d.strike)
         : (parsed ? String(parsed.strike) : null);
      // Prefer the parsed ticker's expiration (a clean yyyy-MM-dd string). d.expiration is the picker
      // descriptor/string (CollarsTransactionDelta.expiration?: any) — String() on an object is "[object
      // Object]", so only fall back to it when there is no parsed option ticker. The TOS-Desktop line
      // builder (makeEquityTosDesktopOrderLine) parses this field as FRIENDLY 'dd-MMM-yy' — feed it ISO
      // and the copied order text reads 'Invalid DateTime' — so normalize ISO -> friendly here.
      const rawExpiration = parsed
         ? parsed.expiration
         : (d.expiration != null ? String(d.expiration) : null);
      const expiration = (rawExpiration && determineDateFormat(rawExpiration) === 'ISO')
         ? convertIsoExpirationToFriendly(rawExpiration)
         : rawExpiration;

      // Limit = the supplied live reference quote for this leg's instrument when positive; else 0 placeholder
      // (the operator then prices it in TOS). The map is keyed by the SAME composed code the delta carries.
      const livePx = prices ? prices[d.tradingInstrumentCode] : undefined;
      const price = (typeof livePx === 'number' && livePx > 0) ? livePx : 0;

      return {
         ticker: d.tradingInstrumentCode,
         action: CollarsCopyToTosAdapter.actionOf(d),
         underlying,
         type,
         strike,
         expiration,
         role: null,
         // Live reference quote when supplied (see `price` above); 0 placeholder otherwise.
         price,
         // Signed magnitude: Buy positive, Sell negative (copyToETS takes |qty| for netPosition + side
         // from action; the sign keeps the leg self-consistent for any downstream re-read).
         qty: d.side === SIDE_BUY ? Math.abs(d.qty) : -Math.abs(d.qty)
      } as SolutionOrderLegDto;
   }

   /**
    * The action string copyToETS keys the Buy/Sell side off of (action.indexOf('Buy'/'Sell')). The Open/
    * Close/More flavour is set from the delta's action for fidelity; only the Buy/Sell token is load-bearing.
    *   Open => To Open · Close/Decrease => To Close · Increase => More.
    */
   private static actionOf(d: CollarsTransactionDelta): SolutionOrderLegActions {
      const buy = d.side === SIDE_BUY;
      switch (d.action) {
         case 'Open':
            return buy ? 'Buy To Open' : 'Sell To Open';
         case 'Increase':
            return buy ? 'Buy More' : 'Sell More';
         case 'Close':
         case 'Decrease':
         default:
            return buy ? 'Buy To Close' : 'Sell To Close';
      }
   }
}
